// Spaces query layer. Pairs with `lib/views/spaces.ts` (server fetchers used
// during SSR prefetch) and `lib/mutations/spaces.ts`. No 'use client'
// directive: hooks are called from client components; server pages import
// the keys for prefetch wiring.
//
// After the 0015 cutover, post/comment query hooks have been folded into
// `lib/queries/feed.ts` — spaces consume the unified feed reads with
// `scope: 'group:<spaceId>'`.

import {
  useQuery,
  useInfiniteQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { components } from '@leanin/api-client';
import { getApiClient } from '../api-client';
import { throwApiError } from '../api-error';

export type SpaceSummary = components['schemas']['SpaceSummary'];
export type SpaceSummaryWithViewerRole =
  components['schemas']['SpaceSummaryWithViewerRole'];
export type SpaceSearchHit = components['schemas']['SpaceSearchHit'];
export type SpaceDetail = components['schemas']['SpaceDetail'];
export type SpaceMember = components['schemas']['GroupMemberView'];

export type SpaceScope = 'all' | 'mine' | 'invited';

export type SpacesListParams = {
  scope?: SpaceScope;
  pageSize?: number;
  /** Honored only when `scope === 'mine'`. Surfaces archived spaces the
   *  viewer leads so they appear under the Mine grid. */
  includeArchived?: boolean;
};

// ---- Query keys ----

export const spacesKeys = {
  all: ['spaces'] as const,
  lists: () => [...spacesKeys.all, 'list'] as const,
  list: (params: SpacesListParams) =>
    [...spacesKeys.lists(), params] as const,
  // PageSize intentionally NOT in the key — it's a fetch parameter, not part
  // of the search-identity.
  search: (q: string) => [...spacesKeys.all, 'search', q] as const,
  detail: (id: string) => [...spacesKeys.all, 'detail', id] as const,
  related: (id: string) =>
    [...spacesKeys.all, 'detail', id, 'related'] as const,
};

// ---- Client fetchers ----

export async function fetchSpaces(
  params: SpacesListParams & { cursor?: string },
) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/spaces', {
    params: {
      query: {
        scope: params.scope,
        pageSize: params.pageSize,
        cursor: params.cursor,
        ...(params.includeArchived ? { includeArchived: true } : {}),
      },
    },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchSpace(
  id: string,
): Promise<SpaceDetail | null> {
  const client = getApiClient();
  const { data, error, response } = await client.GET('/api/v1/spaces/{id}', {
    params: { path: { id } },
  });
  if (response.status === 404) return null;
  if (data === undefined) throwApiError(error);
  return data.data;
}

export async function fetchRelatedSpaces(id: string) {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/spaces/{id}/related',
    { params: { path: { id } } },
  );
  if (data === undefined) throwApiError(error);
  return data.items;
}

export async function fetchSearchSpaces(q: string, pageSize = 24) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/spaces/search', {
    params: { query: { q, pageSize } },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

// ---- Hooks ----

export function useSpaces(
  params: SpacesListParams = {},
  options?: Omit<
    UseQueryOptions<
      Awaited<ReturnType<typeof fetchSpaces>>,
      Error,
      Awaited<ReturnType<typeof fetchSpaces>>
    >,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: spacesKeys.list(params),
    queryFn: () => fetchSpaces(params),
    ...options,
  });
}

export function useInfiniteSpaces(params: SpacesListParams = {}) {
  return useInfiniteQuery({
    queryKey: spacesKeys.list(params),
    queryFn: ({ pageParam }) =>
      fetchSpaces({ ...params, cursor: pageParam ?? undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
}

export function useSpace(id: string) {
  return useQuery({
    queryKey: spacesKeys.detail(id),
    queryFn: () => fetchSpace(id),
  });
}

export function useRelatedSpaces(id: string) {
  return useQuery({
    queryKey: spacesKeys.related(id),
    queryFn: () => fetchRelatedSpaces(id),
  });
}

export function useSearchSpaces(q: string, pageSize = 24) {
  return useQuery({
    queryKey: spacesKeys.search(q),
    queryFn: () => fetchSearchSpaces(q, pageSize),
    enabled: q.trim().length > 0,
  });
}
